import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Ažuriraj stanje tako da sljedeći render prikaže fallback UI.
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error in component tree:", error, errorInfo);
        this.setState({ errorInfo });
        // Ovdje se može dodati slanje grešaka na neki servis (npr. Sentry, Firebase Crashlytics)
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex w-full min-h-[50vh] items-center justify-center p-4">
                    <Card className="max-w-md w-full border-destructive/50 shadow-lg">
                        <CardHeader className="text-center pb-4">
                            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                                <AlertCircle className="w-6 h-6 text-destructive" />
                            </div>
                            <CardTitle className="text-xl">Dogodila se greška</CardTitle>
                            <CardDescription>
                                Nešto nije u redu sa prikazom ovog dijela aplikacije.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="bg-muted p-3 rounded-md text-xs font-mono overflow-auto max-h-32 whitespace-pre-wrap text-muted-foreground border">
                                {this.state.error?.message || "Nepoznata greška prilikom renderiranja komponente."}
                            </div>
                            <Button
                                onClick={() => {
                                    this.setState({ hasError: false, error: null, errorInfo: null });
                                    window.location.reload();
                                }}
                                className="w-full"
                                variant="default"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Osvježi aplikaciju
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}
